import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { tap, catchError, finalize } from 'rxjs/operators';
import { Constants } from '../@classes/constants';
import { AlertController, LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root'
})
export class ApiService {

	constructor(
		private http: HttpClient,
		private alertCtrl: AlertController,
		private translate: TranslateService,
		private loadingCtrl: LoadingController,
	) { }

	/**
	 * Add loading screen to the request, and detect automatically when request is
	 * finished processing things.
	 * @param request 
	 * @param withLoadingScreen 
	 */
	 private applyLoadingScreen(request: Observable<any>, withLoadingScreen: boolean = true) {
		if (!withLoadingScreen) {
			return request;
		}

		let loader = this.loadingCtrl.create({
			message: 'Please wait'
		}).then(async (loadingScreen) => {
			await loadingScreen.present()
			return loadingScreen
		});

		return request
			.pipe(finalize(async () => (await loader).dismiss()))
	}

	private loggingPipe(request: Observable<any>): Observable<any> {
		return request.pipe(
			tap(val => {
				console.log('Response:', val);
			}),
			catchError((err: HttpErrorResponse) => {
				console.log('Error:', err);
				const errorMessage = this.extractErrorMessage(err);
				if (errorMessage && errorMessage != 'reaching-end-of-result') {
					console.log('errorMessage', errorMessage);
					this.message(errorMessage);
				}
				return throwError(() => errorMessage ?? err);
			})
		);
	}

	private extractErrorMessage(err: Partial<HttpErrorResponse> & { error?: any }): string | null {
		const errorMessage = this.extractMessageFromPayload(err?.error);
		if (errorMessage) {
			return errorMessage;
		}

		if (typeof err?.message === 'string' && err.message.trim()) {
			return err.message;
		}

		if (err?.status === 404) {
			return 'resource-not-found';
		}

		if (typeof err?.statusText === 'string' && err.statusText.trim()) {
			return err.statusText;
		}

		return null;
	}

	private extractMessageFromPayload(payload: any): string | null {
		if (!payload) {
			return null;
		}

		if (typeof payload === 'string' && payload.trim()) {
			return payload;
		}

		if (Array.isArray(payload)) {
			for (const item of payload) {
				const nestedMessage = this.extractMessageFromPayload(item);
				if (nestedMessage) {
					return nestedMessage;
				}
			}
			return null;
		}

		if (typeof payload !== 'object') {
			return null;
		}

		const directKeys = ['extras', 'message', 'error', 'detail', 'title'];
		for (const key of directKeys) {
			const nestedMessage = this.extractMessageFromPayload(payload[key]);
			if (nestedMessage) {
				return nestedMessage;
			}
		}

		for (const value of Object.values(payload)) {
			const nestedMessage = this.extractMessageFromPayload(value);
			if (nestedMessage) {
				return nestedMessage;
			}
		}

		return null;
	}

	get(url: string, params: any = {}, auth_token: string = "", withLoadingScreen: boolean = true): Observable<any> {
		let _headers = new HttpHeaders;
		_headers = _headers.set('Authorization', "Bearer " + auth_token);
		let _params = new HttpParams();
		for (let k in params) {
			const value = params[k];
			if (Array.isArray(value)) {
				for (const item of value) {
					_params = _params.append(`${k}[]`, item);
				}
				continue;
			}
			_params = _params.set(k, value);
		}
		let options: any = {};
		if(auth_token != ""){
			options.headers = _headers;	
		}
		options.params = _params;
		let request = this.http.get<any>(Constants.BASE_API_URL + url, options);
		request = this.applyLoadingScreen(request, withLoadingScreen);
		request = this.loggingPipe(request);
		return request;
	}

	postForm(url: string, params: any = {}, auth_token: string = "", withLoadingScreen: boolean = false): Observable<any> {
		let _headers = new HttpHeaders;
		_headers = _headers.set('Authorization', "Bearer " + auth_token);
		let _params = new HttpParams();
		for (let k in params) {
			_params = _params.set(k, params[k]);
		}
		let request;
		if(auth_token == ""){
			request = this.http.post<any>(Constants.BASE_API_URL + url, _params);
		} else {
			request = this.http.post<any>(Constants.BASE_API_URL + url, _params, {headers: _headers});
		}
		request = this.applyLoadingScreen(request, withLoadingScreen);
		request = this.loggingPipe(request);
		return request;
	}

	postFileForm(url: string, params: any = {}, picture: File | null = null, auth_token: string = "", withLoadingScreen: boolean = false): Observable<any> {
		let _headers = new HttpHeaders;
		_headers = _headers.set('Authorization', "Bearer " + auth_token);
		let formData = new FormData();
		for (let k in params) {
			const value = params[k];
			if (value instanceof File) {
				formData.append(k, value, value.name);
			}
			else if (value instanceof Blob) {
				formData.append(k, value);
			}
			else if (value && typeof value === 'object') {
				for (let o in value) {
					formData.append(k + '[]', value[o]);
				}
			}
			else {
				formData.append(k, value);
			}
		}
		if(picture != null) {
			formData.append('picture', picture, picture.name);
		}
		let request = this.http.post<any>(Constants.BASE_API_URL + url, formData, { headers: _headers });
		request = this.applyLoadingScreen(request, withLoadingScreen);
		request = this.loggingPipe(request);
		return request;
	}


	deleteForm(url: string, params: any = {}, withLoadingScreen: boolean = false): Observable<any> {
		let _params = new HttpParams();
		for (let k in params) {
			_params = _params.set(k, params[k]);
		}
		let request = this.http.delete<any>(Constants.BASE_API_URL + url, { params: _params });
		request = this.applyLoadingScreen(request, withLoadingScreen);
		request = this.loggingPipe(request);
		return request;
	}

	async message(message: string) {
		const displayMessage = this.resolveDisplayMessage(message);
		console.log('displayMessage', displayMessage);
		let confirm = this.alertCtrl.create({
			header: this.translate.instant('message'),
			message: displayMessage,
		  buttons: ['OK']
		});
		(await confirm).present();
	  }

	private resolveDisplayMessage(message: string): string {
		const translatedMessage = this.translate.instant(message);
		if (translatedMessage && translatedMessage !== message) {
			return translatedMessage;
		}

		return message;
	}
}
